import React, { useMemo } from 'react';
import type { KnowledgeBaseState } from '../../receptionist-settings/types';
import { RCPT_SETTINGS_KB_CATEGORIES } from '../../receptionist-settings/mockData';
import { Icon } from '../Icon/Icon';
import {
  SettingsMiniStat,
  SettingsPageHeader,
  SettingsSectionHeader,
  SettingsToggle,
} from './SettingsShared';

export interface KnowledgeBaseSettingsPageProps {
  state: KnowledgeBaseState;
  setState: React.Dispatch<React.SetStateAction<KnowledgeBaseState>>;
}

export function KnowledgeBaseSettingsPage({ state, setState }: KnowledgeBaseSettingsPageProps) {
  const { entries, search, activeCategory } = state;
  const setSearch = (v: string) => setState((s) => ({ ...s, search: v }));
  const setCategory = (v: string) => setState((s) => ({ ...s, activeCategory: v }));
  const toggleActive = (id: number, val: boolean) =>
    setState((s) => ({
      ...s,
      entries: s.entries.map((e) => (e.id === id ? { ...e, active: val } : e)),
    }));

  const counts = useMemo(() => {
    const c: Record<string, number> = { all: entries.length };
    RCPT_SETTINGS_KB_CATEGORIES.forEach((cat) => {
      c[cat.id] = entries.filter((e) => e.category === cat.id).length;
    });
    return c;
  }, [entries]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return entries.filter((e) => {
      if (activeCategory !== 'all' && e.category !== activeCategory) return false;
      if (!q) return true;
      return e.title.toLowerCase().includes(q) || e.body.toLowerCase().includes(q);
    });
  }, [entries, search, activeCategory]);

  const grouped = useMemo(() => {
    const g: Record<string, typeof entries> = {};
    RCPT_SETTINGS_KB_CATEGORIES.forEach((cat) => {
      g[cat.id] = [];
    });
    filtered.forEach((e) => {
      const bucket = g[e.category];
      if (bucket) bucket.push(e);
    });
    return g;
  }, [filtered]);

  const totalActive = entries.filter((e) => e.active).length;
  const categoryFilters = [{ id: 'all', label: 'All' }, ...RCPT_SETTINGS_KB_CATEGORIES];

  return (
    <>
      <SettingsPageHeader
        eyebrow="Setup · knowledge"
        title="Knowledge"
        italic="base"
        description="Custom answers Elva uses for practice-specific questions. Her real-time search finds the right entry mid-conversation."
      />

      <div className="set-fade-up set-d-1 mb-[26px] grid grid-cols-3 gap-3">
        <SettingsMiniStat label="Total entries" value={entries.length} />
        <SettingsMiniStat label="Active" value={totalActive} valueColor="var(--set-green-deep)" />
        <SettingsMiniStat label="Categories" value={RCPT_SETTINGS_KB_CATEGORIES.length} valueColor="var(--set-brass-deep)" />
      </div>

      <div className="set-fade-up set-d-2 mb-[18px] flex flex-wrap items-center gap-2.5">
        <div className="relative">
          <Icon
            kind="search"
            size={13}
            stroke={2}
            className="pointer-events-none absolute left-[11px] top-1/2 -translate-y-1/2 text-[var(--ink-3)]"
          />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search entries…"
            className="set-input h-9 w-[280px] pl-[34px] pr-[11px] text-[13px] shadow-[var(--sh-1)]"
          />
        </div>
        <div className="h-[22px] w-px bg-[var(--cream-2)]" />
        {categoryFilters.map((cat) => {
          const isActive = activeCategory === cat.id;
          const count = cat.id === 'all' ? counts.all : counts[cat.id] || 0;
          return (
            <button
              key={cat.id}
              type="button"
              onClick={() => setCategory(cat.id)}
              className="inline-flex h-8 cursor-pointer items-center gap-1.5 rounded-lg border px-[11px] text-[12.5px] shadow-[var(--sh-1)] transition-colors"
              style={{
                background: isActive ? 'var(--set-green-wash)' : 'var(--cream-1)',
                borderColor: isActive ? 'var(--set-green-soft)' : 'var(--cream-2)',
                color: isActive ? 'var(--set-green-deep)' : 'var(--ink-1)',
                fontWeight: isActive ? 600 : 500,
              }}
            >
              <span>{cat.label}</span>
              <span
                className="rounded px-[5px] py-px font-[var(--font-mono)] text-[10px] font-semibold"
                style={{
                  color: isActive ? 'var(--set-green-deep)' : 'var(--ink-3)',
                  background: isActive ? 'var(--set-green-soft)' : 'var(--surface-mute)',
                }}
              >
                {count}
              </span>
            </button>
          );
        })}
        <div className="flex-1" />
        <button type="button" className="set-btn-primary h-[34px]">
          <Icon kind="plus" size={13} stroke={2} />
          Add entry
        </button>
      </div>

      {filtered.length === 0 ? (
        <div className="set-card set-fade-up set-d-3 px-8 py-[60px] text-center">
          <div className="relative mx-auto mb-4 flex h-20 w-20 items-center justify-center">
            <div
              className="absolute inset-0 rounded-[999px] border border-[var(--set-green-soft)] opacity-40"
              style={{ animation: 'set-fade-up 2s ease-out infinite' }}
            />
            <span
              className="relative z-[3] h-2.5 w-2.5 rounded-[999px]"
              style={{ background: 'var(--set-green)' }}
            />
          </div>
          <div className="text-[14px] font-semibold tracking-[-0.005em] text-[var(--ink-0)]">
            {search ? `No entries match "${search}"` : 'No entries in this category yet'}
          </div>
        </div>
      ) : (
        RCPT_SETTINGS_KB_CATEGORIES.map((cat, i) => {
          const items = grouped[cat.id];
          if (!items || items.length === 0) return null;
          return (
            <section
              key={cat.id}
              className="set-fade-up mb-[22px]"
              style={{ animationDelay: `${(i + 2) * 70}ms` }}
            >
              <div className="set-card">
                <SettingsSectionHeader
                  num={String(i + 1).padStart(2, '0')}
                  title={cat.label}
                  sub={`${items.length} entr${items.length === 1 ? 'y' : 'ies'}`}
                />
                {items.map((entry, idx) => (
                  <div
                    key={entry.id}
                    className={[
                      'grid grid-cols-[1fr_auto] items-start gap-4 px-[22px] py-4',
                      idx === items.length - 1 ? '' : 'border-b border-[var(--cream-2)]',
                    ].join(' ')}
                    style={{ opacity: entry.active ? 1 : 0.55 }}
                  >
                    <div className="min-w-0">
                      <div className="mb-1 text-[14px] font-semibold tracking-[-0.005em] text-[var(--ink-0)]">
                        {entry.title}
                      </div>
                      <p className="m-0 mb-1.5 text-[12.5px] leading-[1.55] tracking-[-0.005em] text-[var(--ink-1)]">
                        {entry.body}
                      </p>
                      <div className="flex gap-3 text-[11px] font-medium text-[var(--ink-3)]">
                        <span className="inline-flex items-center gap-1.5">
                          <Icon kind="clock" size={10} stroke={2} />
                          Updated {entry.lastUpdated}
                        </span>
                      </div>
                    </div>
                    <div className="flex shrink-0 items-center gap-2.5">
                      <SettingsToggle value={entry.active} onChange={(v) => toggleActive(entry.id, v)} />
                      <button
                        type="button"
                        title="Edit"
                        className="set-btn inline-flex h-8 w-8 items-center justify-center p-0"
                      >
                        <Icon kind="external" size={13} stroke={2} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          );
        })
      )}
    </>
  );
}
